import _ from "lodash";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { useBalanceStore } from "../../../_store/balanceStore";
import { MTButton } from "../../../components/button/MTButton";
import { HeadWithIcon } from "../../../components/header/Header";
import { ImageMain } from "../../../components/img/Img.Main";
import {
  calDebtCreditCard,
  calTotalCreditorFloatInterest,
  generateAmortizationSchedule,
  round,
  toFloat2,
} from "../../../utils/useFunctions";
import { STYLE_PADDING_MAIN, STYLE_TEXT_CYAN } from "../../../utils/useStyle";
import { OPTIONS_TYPE_CAR, OPTIONS_TYPE_PLACE } from "../../balance/functions";
import { options_debt } from "../../balance/styles/Card";
import { CardDebt } from "../styles/Card";
import { ViewTableDebt } from "./VIDebtTable";
import { useDebtStore } from "../../../_store/debtplanStore";
import { useUserStore } from "../../../_store/userStore";

export const calAmountInterest = (amount, interest, days) => {
  return toFloat2(((amount * interest) / 100) * (days / 365));
};

const ViewDebtPlan = ({ openInfo, setOpenInfo }) => {
  const debtData = useDebtStore((state) => state.debtData);
  const setDebtData = useDebtStore((state) => state.setDebtData);
  // ข้อมูลงบดุล
  const setBalanceData = useBalanceStore((state) => state.setBalanceData);
  const balanceData = useBalanceStore((state) => state.balanceData);
  const userData = useUserStore((state) => state.userData);
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };

  const mergeItems = useMemo(() => {
    let items = [];

    if (balanceData?.debt_long_list?.length > 0) {
      items = [...balanceData?.debt_long_list];
    }

    if (balanceData?.debt_short_list?.length > 0) {
      items = [...items, ...balanceData?.debt_short_list];
    }

    return items;
  }, []);

  const exportToExcel = () => {
    const worksheetData = [];

    // Header rows
    worksheetData.push([
      "ประเภท",
      "ยอดคงค้าง (บาท)",
      "ชำระต่องวด (บาท)",
      "ดอกเบี้ย (%/ปี)",
      "จำนวนงวด",
      "ระยะเวลาหนี้สิน (ปี)",
      "รูปแบบการชำระ",
      "สถานะ"
    ]);

    // รวบรวมข้อมูลหนี้สินระยะสั้น
    if (balanceData?.debt_short_list?.length > 0) {
      worksheetData.push(["หนี้สินระยะสั้น", "", "", "", "", ""]);

      // บัตรเครดิต
      balanceData?.debt_short_list?.forEach((item) => {
        if (item?.options_debt_short === "debt_creditcard") {
          item?.credit_cards?.forEach((item2) => {
            const creditor = calDebtCreditCard(item2);
            const period = creditor?.creditor_items?.length || 0;
            const typePayment =
              item2?.type_payment === "pay_minimum"
                ? "จ่ายขั้นต่ำ"
                : item2?.type_payment === "pay_full"
                ? "จ่ายเต็มจำนวน"
                : "จ่ายแบบระบุจำนวน";
            const typePaymentFormat =
              item2?.type_payment === "pay_minimum"
                ? "จ่ายขั้นต่ำ(รายเดือน)"
                : item2?.type_payment === "pay_full"
                ? "จ่ายเต็มจำนวน"
                : "จ่ายแบบระบุจำนวน(รายเดือน)";
            const years = Math.floor(period / 12);
            const months = period % 12;
            const periodText = years > 0 ? `${years} ปี ${months} เดือน` : `${months} เดือน`;
            const status = options_debt.find(
              (option) => option?.value === item2?.status
            )?.label || "";

            worksheetData.push([
              `บัตรเครดิต ${item2?.creditor_name || ""}`,
              item2?.unpaid_amount || 0,
              item2?.pay_permonth || 0,
              item2?.interest || 0,
              period,
              periodText,
              typePaymentFormat,
              status
            ]);
          });
        }
      });

      // เงินกู้ระยะสั้น
      balanceData?.debt_short_list?.forEach((item) => {
        if (item?.options_debt_short === "debt_shortterm") {
          item?.short_terms?.forEach((item2) => {
            const status = options_debt.find(
              (option) => option?.value === item2?.status
            )?.label || "";
            const period = item2?.period || 0;
            const years = Math.floor(period / 12);
            const months = period % 12;
            const periodText = years > 0 ? `${years} ปี ${months} เดือน` : `${months} เดือน`;

            worksheetData.push([
              item2?.creditor_name || "เงินกู้ระยะสั้น",
              item2?.unpaid_amount || 0,
              item2?.pay_permonth || 0,
              item2?.interest || 0,
              period,
              periodText,
              "รายเดือน",
              status
            ]);
          });
        }
      });

      // หนี้นอกระบบ
      balanceData?.debt_short_list?.forEach((item) => {
        if (item?.options_debt_short === "debt_informal_shortterm") {
          item?.informal_short_terms?.forEach((item2) => {
            const compoundingType =
              item2.compounding_type === "daily"
                ? "รายวัน"
                : item2.compounding_type === "weekly"
                ? "รายสัปดาห์"
                : item2.compounding_type === "monthly"
                ? "รายเดือน"
                : "รายปี";
            const status = options_debt.find(
              (option) => option?.value === item2?.status
            )?.label || "";
            const period = item2?.number_of_days || 0;
            const periodText = `${period} วัน`;

            worksheetData.push([
              `หนี้นอกระบบ (${compoundingType}) ${item2?.creditor_name || ""}`,
              item2?.start_amount || 0,
              item2?.pay_permonth || 0,
              item2?.interest || 0,
              period,
              periodText,
              compoundingType,
              status
            ]);
          });
        }
      });

      // เงินกู้ระยะสั้นอื่นๆ (ดอกลอย)
      balanceData?.debt_short_list?.forEach((item) => {
        if (item?.options_debt_short === "debt_shortterm_other") {
          item?.short_term_others?.forEach((item2) => {
            const status = options_debt.find(
              (option) => option?.value === item2?.status
            )?.label || "";
            const period = item2?.period || 0;
            const years = Math.floor(period / 12);
            const months = period % 12;
            const periodText = years > 0 ? `${years} ปี ${months} เดือน` : `${months} เดือน`;

            worksheetData.push([
              `หนี้กู้ระยะสั้น (ดอกลอย) ${item2?.creditor_name || ""}`,
              item2?.unpaid_amount || 0,
              item2?.pay_permonth || 0,
              item2?.interest || 0,
              period,
              periodText,
              "รายเดือน",
              status
            ]);
          });
        }
      });
    }

    // รวบรวมข้อมูลหนี้สินระยะยาว
    if (balanceData?.debt_long_list?.length > 0) {
      worksheetData.push(["", "", "", "", "", "", "", ""]);
      worksheetData.push(["หนี้สินระยะยาว", "", "", "", "", "", "", ""]);

      // สินเชื่อรถยนต์/จักรยานยนต์
      balanceData?.debt_long_list?.forEach((item) => {
        if (item?.options_debt_long === "debt_car") {
          item?.debt_long_cars?.forEach((item2) => {
            const findTypeCar = OPTIONS_TYPE_CAR?.find(
              (thisType) => thisType.value === item2?.type_car
            );
            const typeCar = findTypeCar?.label || "";
            const status = options_debt.find(
              (option) => option?.value === item2?.status
            )?.label || "";
            const period = item2?.period || 0;
            const years = Math.floor(period / 12);
            const months = period % 12;
            const periodText = years > 0 ? `${years} ปี ${months} เดือน` : `${months} เดือน`;

            worksheetData.push([
              `${typeCar} ${item2?.title_car || ""}`.trim(),
              item2?.unpaid_amount || 0,
              item2?.pay_permonth || 0,
              item2?.interest || 0,
              period,
              periodText,
              "รายเดือน",
              status
            ]);
          });
        }
      });

      // สินเชื่อที่อยู่อาศัย
      balanceData?.debt_long_list?.forEach((item) => {
        if (item?.options_debt_long === "debt_place") {
          item?.debt_long_places?.forEach((item2) => {
            const findTypePlace = OPTIONS_TYPE_PLACE?.find(
              (thisType) => thisType.value === item2?.type_place
            );
            const typePlace = item2?.type_place_other || findTypePlace?.label || "";
            const status = options_debt.find(
              (option) => option?.value === item2?.status
            )?.label || "";

            const title =
              typePlace && item2?.address_place
                ? `${typePlace} ${item2.address_place}`
                : typePlace || item2?.address_place || "ที่อยู่อาศัย";
            
            const period = (item2?.period || 0) * 12;
            const years = Math.floor(period / 12);
            const months = period % 12;
            const periodText = years > 0 ? `${years} ปี ${months} เดือน` : `${months} เดือน`;

            worksheetData.push([
              title,
              item2?.unpaid_amount || 0,
              item2?.pay_permonth || 0,
              item2?.interest || 0,
              period,
              periodText,
              "รายเดือน",
              status
            ]);
          });
        }
      });

      // หนี้สินระยะยาวอื่นๆ
      balanceData?.debt_long_list?.forEach((item) => {
        if (item?.options_debt_long === "debt_long_other") {
          item?.debt_long_others?.forEach((item2) => {
            const status =
              item2?.status === "normal" ? "ปกติ" : item2?.status || "";
            
            const period = (item2?.period || 0) * 12;
            const years = Math.floor(period / 12);
            const months = period % 12;
            const periodText = years > 0 ? `${years} ปี ${months} เดือน` : `${months} เดือน`;

            worksheetData.push([
              item2?.title || "หนี้สินระยะยาวอื่นๆ",
              item2?.unpaid_amount || 0,
              item2?.pay_permonth || 0,
              item2?.interest || 0,
              period,
              periodText,
              "รายเดือน",
              status
            ]);
          });
        }
      });
    }

    // คำนวณยอดรวม
    let totalUnpaidAmount = 0;
    let totalPayPerMonth = 0;

    // รวมยอดหนี้สินระยะสั้น
    balanceData?.debt_short_list?.forEach((item) => {
      if (item?.options_debt_short === "debt_creditcard") {
        item?.credit_cards?.forEach((item2) => {
          totalUnpaidAmount += item2?.unpaid_amount || 0;
          totalPayPerMonth += item2?.pay_permonth || 0;
        });
      }
      if (item?.options_debt_short === "debt_shortterm") {
        item?.short_terms?.forEach((item2) => {
          totalUnpaidAmount += item2?.unpaid_amount || 0;
          totalPayPerMonth += item2?.pay_permonth || 0;
        });
      }
      if (item?.options_debt_short === "debt_informal_shortterm") {
        item?.informal_short_terms?.forEach((item2) => {
          totalUnpaidAmount += item2?.start_amount || 0;
          totalPayPerMonth += item2?.pay_permonth || 0;
        });
      }
      if (item?.options_debt_short === "debt_shortterm_other") {
        item?.short_term_others?.forEach((item2) => {
          totalUnpaidAmount += item2?.unpaid_amount || 0;
          totalPayPerMonth += item2?.pay_permonth || 0;
        });
      }
    });

    // รวมยอดหนี้สินระยะยาว
    balanceData?.debt_long_list?.forEach((item) => {
      if (item?.options_debt_long === "debt_car") {
        item?.debt_long_cars?.forEach((item2) => {
          totalUnpaidAmount += item2?.unpaid_amount || 0;
          totalPayPerMonth += item2?.pay_permonth || 0;
        });
      }
      if (item?.options_debt_long === "debt_place") {
        item?.debt_long_places?.forEach((item2) => {
          totalUnpaidAmount += item2?.unpaid_amount || 0;
          totalPayPerMonth += item2?.pay_permonth || 0;
        });
      }
      if (item?.options_debt_long === "debt_long_other") {
        item?.debt_long_others?.forEach((item2) => {
          totalUnpaidAmount += item2?.unpaid_amount || 0;
          totalPayPerMonth += item2?.pay_permonth || 0;
        });
      }
    });

    // เพิ่มแถวรวม
    worksheetData.push(["", "", "", "", "", "", "", ""]);
    worksheetData.push([
      "รวม",
      totalUnpaidAmount,
      totalPayPerMonth,
      "",
      "",
      "",
      "",
      ""
    ]);

    // สร้าง filename
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear() + 543;
    const dateStr = `${day}${month}${year}`;
    const username = `${userData?.firstname || ""} ${userData?.lastname || ""}`.trim() || "User";
    const fileName = `${username}-debt-${dateStr}`;

    // สร้าง worksheet
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // กำหนดความกว้างคอลัมน์
    ws["!cols"] = [
      { wch: 40 }, // ประเภท
      { wch: 20 }, // ยอดคงค้าง
      { wch: 20 }, // ชำระต่องวด
      { wch: 15 }, // ดอกเบี้ย
      { wch: 15 }, // จำนวนงวด
      { wch: 20 }, // ระยะเวลาหนี้สิน
      { wch: 25 }, // รูปแบบการชำระ
      { wch: 25 }, // สถานะ
    ];

    // สร้าง workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายการหนี้สิน");

    // บันทึกไฟล์
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  return (
    <div className={`${STYLE_PADDING_MAIN} space-y-5 mb-10`}>
      {/*---------------------------------------- ตาราง ----------------------------------------*/}
      <MTButton
            onClick={exportToExcel}
            flexnone="flex-none"
            width="w-fit text-xs"
            text="Excel"
            iconleft
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_6711_51468)">
                  <path
                    d="M14.1733 8.54535H11.6287V7.27304H14.1733V8.54535ZM14.1733 9.27235H11.6287V10.5447H14.1733V9.27235ZM14.1733 3.27441H11.6287V4.5467H14.1733V3.27441ZM14.1733 5.27373H11.6287V6.54601H14.1733V5.27373ZM14.1733 11.2717H11.6287V12.544H14.1733V11.2717ZM15.9309 13.7799C15.8582 14.158 15.4038 14.167 15.1003 14.1798H9.44769V15.8156H8.31897L0 14.3615V1.64045L8.36806 0.18457H9.44769V1.63316H14.9058C15.213 1.64588 15.5511 1.62407 15.8182 1.80763C16.0054 2.07663 15.9873 2.41835 16 2.72732L15.9927 12.1895C15.9837 12.7184 16.0418 13.2583 15.9309 13.7799ZM6.66497 10.9282C6.16334 9.91035 5.65263 8.89976 5.15278 7.88191C5.64716 6.89135 6.13428 5.89716 6.61956 4.90295C6.20697 4.92295 5.79438 4.94838 5.38363 4.97748C5.07644 5.72448 4.71838 6.45151 4.47847 7.22398C4.25491 6.49513 3.95866 5.79354 3.68784 5.08288C3.28797 5.10469 2.88809 5.12832 2.48825 5.15195C2.90991 6.08257 3.35887 7.00038 3.76781 7.93645C3.28616 8.84523 2.83541 9.76676 2.36828 10.681C2.76631 10.6973 3.16438 10.7137 3.56241 10.7191C3.84597 9.99576 4.19856 9.29963 4.44575 8.5617C4.6675 9.35416 5.04372 10.0866 5.35272 10.8445C5.79075 10.8755 6.22694 10.9027 6.66497 10.9282ZM15.1331 2.49638H9.44769V3.27441H10.9017V4.5467H9.44769V5.27373H10.9017V6.54601H9.44769V7.27304H10.9017V8.54535H9.44769V9.27235H10.9017V10.5447H9.44769V11.2717H10.9017V12.544H9.44769V13.3845H15.1331V2.49638Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_6711_51468">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            }
          />
      <ViewTableDebt
        debtShortList={balanceData?.debt_short_list}
        debtLongList={balanceData?.debt_long_list}
        is_display_perMonth={true}
      />

      <HeadWithIcon title={"สถานการณ์ปัจจุบัน "} img={"../images/stg_1.png"} />
      {/*---------------------------------------- หนี้สินระยะสั้น ----------------------------------------*/}
      <>
        <div className=" text-mainblue font-semibold">หนี้สินระยะสั้น</div>
        {balanceData?.debt_short_list?.length > 0 ? (
          <>
            {/*---- บัตรเครดิต ----*/}
            {balanceData?.debt_short_list?.map((item, index) => {
              if (item?.options_debt_short === "debt_creditcard")
                return (
                  <div key={index} className="">
                    {item?.credit_cards?.map((item2, index) => {
                      // เจ้าหนี้ได้เงินทั้งสิน
                      const totalCreditor = () => {
                        let result = 0;
                        result +=
                          item2?.start_amount +
                          (item2?.start_amount * (item2?.interest / 100));
                        return result;
                      };
                      // เป็นดอกเบี้ยเงินกู้
                      const totalInterestLoan = () => {
                        let result = 0;
                        result += totalCreditor() - item2?.start_amount;
                        return result;
                      };

                      const getValueZeroIsNaN = (value) => {
                        if (_.isNaN(value)) return 0;
                        return value || 0;
                      };

                      const creditor = calDebtCreditCard(item2);

                      // console.log("creditor", creditor)
                      // console.log("item2?.unpaid_amount", item2?.unpaid_amount);
                      // console.log("item2?.pay_permonth",item2?.pay_permonth);
                      // console.log("creditor?.creditor_items?.length", creditor?.creditor_items);
                      // console.log("----------------")

                      return (
                        <div key={index} className="mb-4">
                          <CardDebt
                            title={`บัตรเครดิต`}
                            name={item2?.creditor_name}
                            total_start_overdue={item2?.unpaid_amount}
                            total_overdue={item2?.unpaid_amount}
                            onClick={() => {
                              setOpenInfo(!openInfo);
                            }}
                            options_debt_short="creditcard"
                            isCreditCard
                            interest_loan_peryear={item2?.interest}
                            total_payment={item2?.pay_permonth}
                            period={Math.ceil(
                              item2?.unpaid_amount / item2?.pay_permonth
                            )}
                            periodCustom={creditor?.creditor_items?.length}
                            // periodCustom={0}
                            total_creditor={creditor.total_creditor}
                            total_interest_loan={
                              creditor.total_creditor_interest
                            }
                            type_payment_ori={item2?.type_payment}
                            total_loan={item2?.unpaid_amount}
                            type_payment={
                              item2?.type_payment === "pay_minimum"
                                ? "จ่ายขั้นต่ำ"
                                : item2?.type_payment === "pay_full"
                                ? "จ่ายเต็มจำนวน"
                                : "จ่ายแบบระบุจำนวน"
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                );
            })}
            {/*---- เงินกู้ระยะสั้น ----*/}
            {balanceData?.debt_short_list?.map((item, index) => {
              if (item?.options_debt_short === "debt_shortterm")
                return (
                  <div key={index} className="">
                    {item?.short_terms?.map((item2, index) => {
                      const interest_min = toFloat2(
                        (item2.unpaid_amount * item2.interest) / 100
                      );

                      const loanDetails = generateAmortizationSchedule(
                        item2.unpaid_amount,
                        item2.interest,
                        item2.pay_permonth
                      ).filter((item) => {
                        return item?.interestPayment > 0;
                      });

                      const totalInterest = loanDetails?.reduce((acc, item) => {
                        return acc + item?.interestPayment;
                      }, 0);
                      const totalCreditor = round(
                        item2?.unpaid_amount + totalInterest,
                        0
                      );

                      return (
                        <div key={index} className="mb-4">
                          <CardDebt
                            title={`หนี้กู้ระยะสั้น`}
                            name={item2?.creditor_name}
                            total_start_overdue={item2?.unpaid_amount}
                            total_overdue={item2?.unpaid_amount}
                            onClick={() => {
                              setOpenInfo(!openInfo);
                            }}
                            interest_loan_peryear={item2?.interest}
                            total_payment={item2?.pay_permonth}
                            period={item2?.period || 0}
                            total_creditor={item2?.pay_permonth * item2?.period}
                            // total_interest_loan={round(totalInterest, 0)}
                            total_interest_loan={totalInterest}
                            total_loan={item2?.unpaid_amount}
                            type_payment={
                              item2?.type_payment === "pay_minimum"
                                ? "จ่ายขั้นต่ำ"
                                : item2?.type_payment === "pay_full"
                                ? "จ่ายเต็มจำนวน"
                                : "จ่ายแบบระบุจำนวน"
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                );
            })}

            {/*---- หนี้นอกระบบ ----*/}
            {balanceData?.debt_short_list?.map((item, index) => {
              if (item?.options_debt_short === "debt_informal_shortterm")
                return (
                  <div key={index} className="">
                    {item?.informal_short_terms?.map((item2, index) => {
                      // เจ้าหนี้ได้เงินทั้งสิน
                      const totalCreditor = () => {
                        // let result = 0;
                        // result +=
                        //   item2?.unpaid_amount +
                        //   item2?.unpaid_amount * (item2?.interest / 100);
                        // return result
                        return item2?.start_amount + totalInterestLoan();
                      };

                      // เป็นดอกเบี้ยเงินกู้
                      const totalInterestLoan = () => {
                        // let result = 0;
                        // result += totalCreditor() - item2?.unpaid_amount;
                        // return result;
                        const interest_period =
                          item2?.unpaid_amount * (item2.interest / 100);
                        let interest = 0;
                        let period = 1;
                        if (item2.compounding_type === "daily") {
                          period = 365;
                        } else if (item2.compounding_type === "weekly") {
                          period = 52;
                        } else if (item2.compounding_type === "monthly") {
                          period = 12;
                        }

                        interest = interest_period * period;
                        return interest;
                      };

                      const getCompoundingType = () => {
                        if (item2.compounding_type === "daily") {
                          return "รายวัน";
                        } else if (item2.compounding_type === "weekly") {
                          return "รายสัปดาห์";
                        } else if (item2.compounding_type === "monthly") {
                          return "รายเดือน";
                        }
                        return "รายปี";
                      };

                      return (
                        <div key={index} className="mb-4">
                          <CardDebt
                            title={`หนี้นอกระบบ (รายวัน)`}
                            name={item2?.creditor_name}
                            total_start_overdue={item2?.unpaid_amount}
                            total_overdue={item2?.start_amount}
                            onClick={() => {
                              setOpenInfo(!openInfo);
                            }}
                            number_of_days={item2?.number_of_days}
                            compounding_type={getCompoundingType()}
                            interest_loan_peryear={item2?.interest}
                            total_payment={item2?.pay_permonth}
                            period={item2?.period || 0}
                            total_creditor={
                              item2.pay_permonth * item2?.number_of_days
                            }
                            total_interest_loan={
                              item2.pay_permonth * item2?.number_of_days -
                              item2?.start_amount
                            }
                            total_loan={item2?.start_amount}
                            type_payment={""}
                            pay_permonth={item2?.pay_permonth}
                            options_debt_short={item?.options_debt_short}
                            year={item2?.year}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
            })}

            {/*---- เงินกู้ระยะสั้นอื่น ๆ ----*/}
            {balanceData?.debt_short_list?.map((item, index) => {
              if (item?.options_debt_short === "debt_shortterm_other")
                return (
                  <div key={index} className="">
                    {item?.short_term_others?.map((item2, index) => {
                      const creditor = calTotalCreditorFloatInterest(item2);

                      // 6533
                      const totalInterest = creditor?.reduce((acc, item) => {
                        return acc + item.interest;
                      }, 0);

                      const totalCreditorInterest = creditor?.reduce(
                        (acc, item, index) => {
                          if (index > 11) {
                            return acc;
                          }
                          return acc + item.interest;
                        },
                        0
                      );

                      return (
                        <div key={index} className="mb-4">
                          <CardDebt
                            title={`หนี้กู้ระยะสั้น (ดอกลอย)`}
                            name={item2?.creditor_name}
                            options_debt_short="debt_shortterm_other"
                            total_start_overdue={item2?.unpaid_amount}
                            total_overdue={item2?.unpaid_amount}
                            onClick={() => {
                              setOpenInfo(!openInfo);
                            }}
                            pay_permonth={item2?.pay_permonth}
                            interest_loan_peryear={item2?.interest}
                            total_payment={item2?.pay_permonth}
                            period={item2?.period || 0}
                            total_creditor={
                              item2?.unpaid_amount + totalCreditorInterest
                            }
                            total_interest_loan={totalCreditorInterest}
                            total_loan={item2?.unpaid_amount}
                            total_creditor2={
                              item2?.unpaid_amount + totalInterest
                            }
                            total_interest_loan2={totalInterest}
                            total_loan2={item2?.unpaid_amount}
                            type_payment={""}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
            })}
          </>
        ) : (
          <div className="text-center">ไม่มีรายการ</div>
        )}
      </>

      {/*---------------------------------------- หนี้สินระยะยาว ----------------------------------------*/}
      <>
        <div className=" text-mainblue font-semibold">หนี้สินระยะยาว</div>
        {balanceData?.debt_long_list?.length > 0 ? (
          <>
            {/*---- สินเชื่อรถยนต์ / จักรยานยนต์ ----*/}
            {balanceData?.debt_long_list?.map((item, index) => {
              if (item?.options_debt_long === "debt_car")
                return (
                  <div key={index} className="">
                    {item?.debt_long_cars?.map((item2, index) => {
                      const findTypeCar = OPTIONS_TYPE_CAR?.filter(
                        (thisType) => {
                          if (thisType.value === item2?.type_car)
                            return thisType;
                        }
                      );
                      const typeCar = findTypeCar?.[0]?.label;
                      // เจ้าหนี้ได้เงินทั้งสิน
                      const totalCreditor = () => {
                        let result = 0;
                        result +=
                          item2?.unpaid_amount +
                          item2?.unpaid_amount * (item2?.interest / 100);
                        return result;
                      };
                      // เป็นดอกเบี้ยเงินกู้
                      const totalInterestLoan = () => {
                        let result = 0;
                        result += totalCreditor() - item2?.unpaid_amount;
                        return result;
                      };

                      const getTypePayment = () => {
                        const findOptionPayment = options_debt.find(
                          (debtItem) => {
                            return debtItem.value === item2?.status;
                          }
                        );
                        return findOptionPayment?.label;
                      };

                      let total_creditor = item2?.pay_permonth * item2?.period;
                      let total_interest_loan =
                        total_creditor - item2?.unpaid_amount;

                      return (
                        <div key={index} className="mb-4">
                          <CardDebt
                            title={
                              typeCar
                                ? `${typeCar} ${item2?.title_car}`
                                : item2?.title_car
                            }
                            total_start_overdue={item2?.unpaid_amount}
                            total_overdue={item2?.unpaid_amount}
                            onClick={() => {
                              setOpenInfo(!openInfo);
                            }}
                            // periodCustom={`${item2?.period} ปี`}
                            interest_loan_peryear={item2?.interest}
                            total_payment={item2?.pay_permonth}
                            period={item2?.period || 0}
                            total_creditor={total_creditor}
                            total_interest_loan={total_interest_loan}
                            total_loan={item2?.unpaid_amount}
                            type_payment={getTypePayment()}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
            })}

            {/*---- สินเชื่อที่อยู่อาศัย ----*/}
            {balanceData?.debt_long_list?.map((item, index) => {
              if (item?.options_debt_long === "debt_place")
                return (
                  <div key={index} className="">
                    {item?.debt_long_places?.map((item2, index) => {
                      const findTypePlace = OPTIONS_TYPE_PLACE?.filter(
                        (thisType) => {
                          if (thisType.value === item2?.type_place)
                            return thisType;
                        }
                      );
                      const typePlace = item2?.type_place_other
                        ? item2?.type_place_other
                        : findTypePlace?.[0]?.label;
                      // เจ้าหนี้ได้เงินทั้งสิน
                      const totalCreditor = () => {
                        let result = 0;
                        result +=
                          item2?.start_amount +
                          item2?.start_amount * (item2?.interest / 100);
                        return result;
                      };
                      // เป็นดอกเบี้ยเงินกู้
                      const totalInterestLoan = () => {
                        let result = 0;
                        result += totalCreditor() - item2?.start_amount;
                        return result;
                      };

                      const getTypePayment = () => {
                        const findOptionPayment = options_debt.find(
                          (debtItem) => {
                            return debtItem.value === item2?.status;
                          }
                        );
                        return findOptionPayment?.label;
                      };

                      let total_creditor =
                        item2?.pay_permonth * item2?.period * 12;
                      let total_interest_loan =
                        total_creditor - item2?.unpaid_amount;

                      return (
                        <div key={index} className="mb-4">
                          <CardDebt
                            title={
                              typePlace
                                ? item2?.address_place !== "" ||
                                  item2?.address_place !== undefined
                                  ? `${typePlace} ${item2?.address_place}`
                                  : typePlace
                                : item2?.address_place === "" ||
                                  item2?.address_place === undefined
                                ? "ที่อยู่อาศัย"
                                : item2?.address_place 
                            }
                            total_start_overdue={item2?.unpaid_amount}
                            total_overdue={item2?.unpaid_amount}
                            onClick={() => {
                              setOpenInfo(!openInfo);
                            }}
                            typeDebt="place"
                            interest_loan_peryear={item2?.interest}
                            total_payment={item2?.pay_permonth}
                            period={item2?.period * 12 || 0}
                            total_creditor={total_creditor}
                            total_interest_loan={total_interest_loan}
                            total_loan={item2?.unpaid_amount}
                            type_payment={getTypePayment()}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
            })}

            {/*---- หนี้สินระยะยาวอื่น ๆ ----*/}
            {balanceData?.debt_long_list?.map((item, index) => {
              if (item?.options_debt_long === "debt_long_other")
                return (
                  <div key={index} className="">
                    {item?.debt_long_others?.map((item2, index) => {
                      // เจ้าหนี้ได้เงินทั้งสิน
                      const totalCreditor = () => {
                        let result = 0;
                        result +=
                          item2?.start_amount +
                          item2?.start_amount * (item2?.interest / 100);
                        return result;
                      };
                      // เป็นดอกเบี้ยเงินกู้
                      const totalInterestLoan = () => {
                        let result = 0;
                        result += totalCreditor() - item2?.start_amount;
                        return result;
                      };
                      return (
                        <div key={index} className="mb-4">
                          <CardDebt
                            title={item2?.title}
                            total_start_overdue={item2?.unpaid_amount}
                            total_overdue={item2?.unpaid_amount}
                            onClick={() => {
                              setOpenInfo(!openInfo);
                            }}
                            typeDebt="long_other"
                            interest_loan_peryear={item2?.interest}
                            total_payment={item2?.pay_permonth}
                            period={item2?.period * 12 || 0}
                            total_creditor={totalCreditor()}
                            total_interest_loan={totalInterestLoan()}
                            total_loan={item2?.start_amount}
                            type_payment={
                              item2?.status === "normal"
                                ? "ปกติ"
                                : item2?.status
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                );
            })}
          </>
        ) : (
          <div className="text-center">ไม่มีรายการ</div>
        )}
      </>
      <div className="flex flex-col items-center w-full">
        <div className=" mt-5">
          <div className=" flex justify-center items-center text-mainblue  font-semibold text-xl text-center ">
            <ImageMain src="../images/taxicon3.png" className=" w-8 h-8" />
            ไม่ต้องเครียด
          </div>
          <div className={` text-mainblue  text-base text-center mt-1`}>
            คุณสามารถจัดการหนี้สินได้ด้วยการจัดการของเรา
          </div>
          <div className={`${STYLE_TEXT_CYAN}  text-center text-2xl mt-2`}>
            มาวางแผนหนี้สินกันเถอะ !
          </div>
        </div>
      </div>

      <MTButton
        text={"วางแผนหนี้สิน"}
        disabled={
          !balanceData?.debt_short_list?.length &&
          !balanceData?.debt_long_list?.length
        }
        color="bg-maincyan"
        width="w-full h-12"
        onClick={() => {
          if (
            !balanceData?.debt_short_list?.length &&
            !balanceData?.debt_long_list?.length
          ) {
            return;
          }
          setDebtData({
            ...debtData,
            is_plan: true,
          });
          scrollToTop();
          navigate("/debtplan/form");
        }}
      />

      {!mergeItems?.length && (
        <div className="flex flex-col text-center text-[13px] font-medium">
          “กรุณากรอข้อมูลใน งบดุล {">"} หนี้สิน {">"}{" "}
          หนี้สินระยะสั้น/หนี้สินระยะยาว
          เพื่อให้ระบบมีข้อมูลในการวิเคราะห์การวางแผนหนี้สินของคุณ”
        </div>
      )}
    </div>
  );
};
export default ViewDebtPlan;
